import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AgGridReact } from 'ag-grid-react'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { MdArrowBack } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaTimes } from 'react-icons/fa'
import {
  Add,
  FileUploadOutlined,
  ReplayOutlined,
  Visibility
} from '@mui/icons-material'
import AppButton from '../../shared/AppButton'
import CustomFilterInput from '../../shared/CustomFilterInput'
import AppModal from '../../components/AppModal'
import Pagination from '../../components/Pagination'
import AppTable from '../../shared/AppTable'
import TableSkeletonLoader from '../../common/TableLoading'
import { API_BASE_URL } from '../../config/apiConfig'
import claims from '../../gql/claims'
import BulkReplaceTabs from './components/BulkReplaceTabs'
import ClaimsBulkSidebarLayout from './components/ClaimsBulkSidebarLayout'

ModuleRegistry.registerModules([AllCommunityModule])

const DEFAULT_GRID_ROWS = 100

const emptyReplacement = () => ({
  old_standard_code: '',
  new_standard_code: '',
  new_standard_description: '',
  old_non_standard_code: '',
  new_non_standard_code: '',
  new_non_standard_description: ''
})

const createGridRows = () =>
  Array.from({ length: DEFAULT_GRID_ROWS }, (_, index) => ({
    ...emptyReplacement(),
    row_no: index + 1
  }))

const defaultDateRange = () => {
  const toDateInput = date => date.toISOString().slice(0, 10)
  const toDate = new Date()
  const fromDate = new Date()
  fromDate.setMonth(fromDate.getMonth() - 1)
  return { fromDate: toDateInput(fromDate), toDate: toDateInput(toDate) }
}

const getMessage = error =>
  error?.graphQLErrors?.[0]?.message ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Something went wrong. Please try again.'

const formatDate = value => (value ? new Date(value).toLocaleString() : '—')

function SummaryCard({ label, value }) {
  return (
    <div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
      <p className='text-[10px] font-medium uppercase tracking-wide text-slate-500'>
        {label}
      </p>
      <p className='mt-1 text-base font-semibold text-slate-800'>
        {value ?? 0}
      </p>
    </div>
  )
}

function ActiveJobCard({ job, onStopPolling }) {
  const navigate = useNavigate()
  const [pollPage] = useState(1)
  const [stopped, setStopped] = useState(false)
  const limit = 10

  const { data } = useQuery(claims.GET_CLAIM_ITEM_BULK_LOG_DETAILS, {
    variables: { jobId: job.job_id, page: pollPage, limit },
    pollInterval: stopped ? 0 : 15000,
    skip: stopped,
    fetchPolicy: 'network-only'
  })

  const details = data?.getClaimItemBulkLogDetails
  const currentJob = details?.job || job
  const status = currentJob.status || 'processing'

  useEffect(() => {
    if (!stopped && ['completed', 'failed', 'reverted'].includes(status)) {
      setStopped(true)
      onStopPolling(currentJob)
    }
  }, [status, currentJob, onStopPolling, stopped])

  const stats = [
    ['Claims Scanned', currentJob.total_claim_requests_scanned],
    ['Claims Updated', currentJob.total_claims_updated],
    ['Items Matched', currentJob.total_items_matched],
    ['Items Updated', currentJob.total_items_updated],
    ['Skipped', currentJob.skipped_count]
  ]

  return (
    <div className='mt-2 rounded-md border border-blue-200 bg-blue-50 p-3'>
      <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
        <div>
          <div className='flex items-center gap-2'>
            <span className='inline-block h-2 w-2 animate-ping rounded-full bg-blue-600' />
            <p className='text-xs font-semibold text-blue-900'>
              Bulk Item Replace Job is processing... ({status})
            </p>
          </div>
          <p className='mt-0.5 text-[11px] text-blue-700'>
            Job ID: {currentJob.job_id} · Date Range: {currentJob.from_date || '—'} — {currentJob.to_date || '—'}
          </p>
          {currentJob.error && (
            <p className='mt-1 text-xs text-red-600 font-medium'>
              Error: {currentJob.error}
            </p>
          )}
        </div>
        <AppButton
          label='View Job Details'
          variant='outlined'
          bgColor='transparent'
          onClick={() =>
            navigate(`/claims/item-bulk-replace/job/${currentJob.job_id}`)
          }
        />
      </div>
      <div className='grid grid-cols-2 gap-2 md:grid-cols-5'>
        {stats.map(([label, value]) => (
          <SummaryCard key={label} label={label} value={value} />
        ))}
      </div>
    </div>
  )
}

export default function ClaimItemBulkReplace() {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const navigate = useNavigate()
  const location = useLocation()
  const gridRef = useRef(null)
  const isReplacePage = location.pathname.endsWith('/replace')
  const defaultDates = useMemo(defaultDateRange, [])
  const [inputMode, setInputMode] = useState('manual')
  const [fromDate, setFromDate] = useState(defaultDates.fromDate)
  const [toDate, setToDate] = useState(defaultDates.toDate)
  const [replacements, setReplacements] = useState(createGridRows)
  const [file, setFile] = useState(null)
  const [activeJob, setActiveJob] = useState(null)
  const [logsPage, setLogsPage] = useState(1)
  const [confirmRevert, setConfirmRevert] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const [runReplace, { loading: replacing }] = useMutation(
    claims.BULK_REPLACE_CLAIM_ITEMS
  )
  const [runRevert, { loading: reverting }] = useMutation(
    claims.REVERT_CLAIM_ITEM_BULK_REPLACE
  )
  const [
    loadLogs,
    { data: logsData, loading: loadingLogs, refetch: refetchLogs }
  ] = useLazyQuery(claims.LIST_CLAIM_ITEM_BULK_LOGS, {
    fetchPolicy: 'network-only',
    onError: error => toast.error(getMessage(error))
  })

  const logs = logsData?.listClaimItemBulkLogs?.data || []
  const logPages = logsData?.listClaimItemBulkLogs?.pages || 1

  const [selectedFieldKeys, setSelectedFieldKeys] = useState([
    'standard_replacement'
  ])
  const [isFieldMenuOpen, setIsFieldMenuOpen] = useState(false)
  const fieldMenuRef = useRef(null)
  const addFieldButtonRef = useRef(null)

  const FIELD_GROUPS = useMemo(
    () => [
      {
        key: 'standard_replacement',
        title: 'Standard Replacement',
        fields: [
          { headerName: 'Old Code', field: 'old_standard_code', editable: true, flex: 1, minWidth: 120, cellClass: 'bg-gray-50' },
          { headerName: 'New Code', field: 'new_standard_code', editable: true, flex: 1, minWidth: 120, cellClass: 'bg-white' },
          { headerName: 'New Description', field: 'new_standard_description', editable: true, flex: 2, minWidth: 180, cellClass: 'bg-white' }
        ]
      },
      {
        key: 'non_standard_replacement',
        title: 'Non-Standard Replacement',
        fields: [
          { headerName: 'Old Code', field: 'old_non_standard_code', editable: true, flex: 1, minWidth: 120, cellClass: 'bg-gray-50' },
          { headerName: 'New Code', field: 'new_non_standard_code', editable: true, flex: 1, minWidth: 120, cellClass: 'bg-white' },
          { headerName: 'New Description', field: 'new_non_standard_description', editable: true, flex: 2, minWidth: 180, cellClass: 'bg-white' }
        ]
      }
    ],
    []
  )

  useEffect(() => {
    if (!isFieldMenuOpen) return

    const handleOutsideClick = event => {
      if (
        fieldMenuRef.current?.contains(event.target) ||
        addFieldButtonRef.current?.contains(event.target)
      ) {
        return
      }
      setIsFieldMenuOpen(false)
    }

    const handleEscape = event => {
      if (event.key === 'Escape') setIsFieldMenuOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isFieldMenuOpen])

  const handleAddFieldGroup = key => {
    setSelectedFieldKeys(prev => (prev.includes(key) ? prev : [...prev, key]))
    setIsFieldMenuOpen(false)
  }

  const handleRemoveFieldGroup = useCallback(key => {
    setSelectedFieldKeys(prev => prev.filter(fieldKey => fieldKey !== key))
  }, [])

  const selectedFieldGroups = useMemo(
    () =>
      selectedFieldKeys
        .map(key => FIELD_GROUPS.find(group => group.key === key))
        .filter(Boolean),
    [selectedFieldKeys, FIELD_GROUPS]
  )

  const mappingColumns = useMemo(
    () => [
      {
        headerName: '#',
        field: 'row_no',
        width: 48,
        minWidth: 48,
        maxWidth: 56,
        editable: false,
        filter: false,
        cellClass: 'text-center text-xs text-gray-500'
      },
      ...selectedFieldGroups.map(group => ({
        headerName: group.title,
        flex: 1,
        marryChildren: true,
        headerGroupComponent: () => (
          <div className='flex h-full w-full items-center justify-between gap-2 px-1'>
            <span className='truncate font-semibold'>{group.title}</span>
            <button
              type='button'
              className='inline-flex h-5 w-5 flex-none items-center justify-center rounded text-gray-500 hover:bg-red-50 hover:text-red-600'
              title='Remove'
              onClick={e => {
                e.stopPropagation()
                handleRemoveFieldGroup(group.key)
              }}
            >
              <FaTimes size={10} />
            </button>
          </div>
        ),
        children: group.fields
      })),
      {
        headerName: 'Actions',
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        editable: false,
        sortable: false,
        filter: false,
        resizable: false,
        headerComponent: () => (
          <div className='flex items-center gap-1.5 w-full'>
            <button
              ref={addFieldButtonRef}
              type='button'
              className='inline-flex h-5 w-5 flex-none items-center justify-center rounded border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
              title='Add Field'
              onClick={() => setIsFieldMenuOpen(prev => !prev)}
            >
              <FaPlus size={10} />
            </button>
            <span className='text-xs font-semibold'>Actions</span>
          </div>
        ),
        cellClass: 'flex items-center justify-center text-center',
        cellRenderer: params => (
          <button
            type='button'
            onClick={() =>
              setReplacements(rows =>
                rows.length > 1
                  ? rows
                    .filter((_, index) => index !== params.node.rowIndex)
                    .map((row, index) => ({ ...row, row_no: index + 1 }))
                  : rows
              )
            }
            disabled={replacements.length === 1}
            className='inline-flex h-5 w-5 items-center justify-center rounded text-red-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-slate-300'
            title='Remove Row'
          >
            <FaTimes size={11} />
          </button>
        )
      }
    ],
    [selectedFieldGroups, handleRemoveFieldGroup, replacements.length]
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      editable: true,
      minWidth: 96,
      width: 112
    }),
    []
  )

  const logColumns = useMemo(
    () => [
      {
        header: 'Actions',
        align: 'center',
        render: job => (
          <div
            className='grid gap-x-2 w-max'
            style={{ gridTemplateColumns: 'repeat(2, max-content)' }}
          >
            <button
              type='button'
              title='View details'
              onClick={() =>
                navigate('/claims/item-bulk-replace/job/' + job.job_id)
              }
              className='text-gray-500 hover:text-blue-600'
            >
              <Visibility sx={{ fontSize: 19 }} />
            </button>
            {/* {job.status === 'completed' && !job.reverted_by_job_id && job?.job_type !== 'revert' && (
              <button
                type='button'
                title='Revert'
                onClick={() => setConfirmRevert(job)}
                className='text-red-500 hover:text-red-700'
              >
                <ReplayOutlined sx={{ fontSize: 19 }} />
              </button>
            )} */}
          </div>
        )
      },
      { header: 'Type', field: 'job_type' },
      {
        header: 'Status',
        render: job => {
          let bgClass = 'bg-slate-100 text-slate-600'
          if (job.status === 'completed') bgClass = 'bg-emerald-100 text-emerald-700'
          else if (job.status === 'processing') bgClass = 'bg-blue-100 text-blue-700'
          else if (job.status === 'failed') bgClass = 'bg-red-100 text-red-700'
          else if (job.status === 'reverted') bgClass = 'bg-amber-100 text-amber-700'
          return (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${bgClass}`}>
              {job.status || '—'}
            </span>
          )
        }
      },
      {
        header: 'Date Range',
        render: job => `${job.from_date || '—'} — ${job.to_date || '—'}`
      },
      {
        header: 'Mappings',
        field: 'replacement_count',
        align: 'center'
      },
      {
        header: 'Claims Updated',
        field: 'total_claims_updated',
        align: 'center'
      },
      {
        header: 'Items Updated',
        field: 'total_items_updated',
        align: 'center'
      },
      { header: 'Created At', render: job => formatDate(job.created_at) }
    ],
    [navigate]
  )

  const fetchLogs = useCallback(
    (page = 1) => {
      setLogsPage(page)
      return loadLogs({ variables: { input: { page, limit: 10 } } })
    },
    []
  )

  useEffect(() => {
    if (!isReplacePage) {
      loadLogs({ variables: { input: { page: 1, limit: 10 } } })
    }
  }, [isReplacePage, loadLogs])

  const validateCommon = () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both request dates.')
      return false
    }
    if (fromDate > toDate) {
      toast.error('From Date cannot be after To Date.')
      return false
    }
    return true
  }

  const saveJobStarted = jobData => {
    if (!jobData?.job_id) {
      throw new Error('The bulk replace process did not return a job ID.')
    }
    toast.success('Claim item replacement job started successfully.')
    navigate('/claims/item-bulk-replace')
  }

  const handleStopPolling = useCallback(finalJob => {
    setActiveJob(null)
    if (finalJob.status === 'completed') {
      toast.success('Bulk replace job completed successfully!')
    } else if (finalJob.status === 'failed') {
      toast.error(`Bulk replace job failed: ${finalJob.error || 'Unknown error'}`)
    }
  }, [])

  const submitManual = async () => {
    if (!validateCommon()) return

    const gridApi = gridRef.current?.api
    if (gridApi) gridApi.stopEditing()

    const currentRows = []
    gridApi?.forEachNode(node => currentRows.push(node.data))

    const stdFields = ['old_standard_code', 'new_standard_code', 'new_standard_description']
    const nonStdFields = ['old_non_standard_code', 'new_non_standard_code', 'new_non_standard_description']
    const allFields = [...stdFields, ...nonStdFields]

    const populatedRows = currentRows.filter(row =>
      allFields.some(field => String(row[field] || '').trim())
    )

    if (!populatedRows.length) {
      toast.error('Enter at least one replacement row.')
      return
    }

    const payloadReplacements = []

    for (let i = 0; i < populatedRows.length; i++) {
      const row = populatedRows[i]
      const hasStd = stdFields.some(field => String(row[field] || '').trim())
      const hasNonStd = nonStdFields.some(field => String(row[field] || '').trim())

      if (!hasStd && !hasNonStd) continue

      if (hasStd) {
        const stdComplete = stdFields.every(field => String(row[field] || '').trim())
        if (!stdComplete) {
          toast.error(
            `Row #${i + 1}: Standard replacement requires Old Standard Code, New Standard Code, and New Standard Description.`
          )
          return
        }
      }

      if (hasNonStd) {
        const nonStdComplete = nonStdFields.every(field => String(row[field] || '').trim())
        if (!nonStdComplete) {
          toast.error(
            `Row #${i + 1}: Non-standard replacement requires Old Non-Standard Code, New Non-Standard Code, and New Non-Standard Description.`
          )
          return
        }
      }

      const itemPayload = {}
      if (hasStd) {
        itemPayload.old_standard_code = row.old_standard_code.trim()
        itemPayload.new_standard_code = row.new_standard_code.trim()
        itemPayload.new_standard_description = row.new_standard_description.trim()
      }
      if (hasNonStd) {
        itemPayload.old_non_standard_code = row.old_non_standard_code.trim()
        itemPayload.new_non_standard_code = row.new_non_standard_code.trim()
        itemPayload.new_non_standard_description = row.new_non_standard_description.trim()
      }

      payloadReplacements.push(itemPayload)
    }

    try {
      const response = await runReplace({
        variables: {
          input: {
            from_date: fromDate,
            to_date: toDate,
            replacements: payloadReplacements
          }
        }
      })
      const payload = response.data?.bulkReplaceClaimItems
      if (String(payload?.status) !== '202' && String(payload?.status) !== '200') {
        throw new Error(payload?.message)
      }
      saveJobStarted(payload.data)
    } catch (error) {
      toast.error(getMessage(error))
    }
  }

  const submitExcel = async () => {
    if (!validateCommon()) return
    if (!file) {
      toast.error('Choose an Excel file before submitting.')
      return
    }
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error('Please upload an .xlsx or .xls file.')
      return
    }
    const token = localStorage.getItem('token')
    if (!token) {
      toast.error('Your session has expired. Please log in again.')
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    formData.append('from_date', fromDate)
    formData.append('to_date', toDate)
    try {
      setUploading(true)
      const response = await axios.post(
        `${API_BASE_URL}/claim/item/bulk-replace`,
        formData,
        { headers: { Authorization: `Bearer ${token.replace(/"/g, '')}` } }
      )
      const payload = response.data?.data || response.data
      if (
        String(response.data?.status || '202') !== '202' &&
        String(response.data?.status || '200') !== '200'
      ) {
        throw new Error(response.data?.message || response.data?.error)
      }
      saveJobStarted(payload)
      setFile(null)
    } catch (error) {
      toast.error(getMessage(error))
    } finally {
      setUploading(false)
    }
  }

  const downloadTemplate = async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      toast.error('Your session has expired. Please log in again.')
      return
    }
    try {
      setDownloadingTemplate(true)
      const response = await axios.get(
        `${API_BASE_URL}/claim/item/bulk-replace/template`,
        {
          headers: { Authorization: `Bearer ${token.replace(/"/g, '')}` },
          responseType: 'blob'
        }
      )
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'claim_item_bulk_replace_template.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(getMessage(error))
    } finally {
      setDownloadingTemplate(false)
    }
  }

  const handleFileDrop = e => {
    e.preventDefault()
    setDragOver(false)
    const droppedFile = e.dataTransfer?.files?.[0]
    if (droppedFile && /\.xlsx?$/i.test(droppedFile.name)) {
      setFile(droppedFile)
    } else if (droppedFile) {
      toast.error('Please upload an .xlsx or .xls file.')
    }
  }

  const revertJob = async () => {
    if (!confirmRevert) return
    try {
      const response = await runRevert({
        variables: {
          input: {
            job_id: confirmRevert.job_id,
            batch_size: Number(confirmRevert.batch_size) || 200
          }
        }
      })
      const payload = response.data?.revertClaimItemBulkReplace
      if (String(payload?.status) !== '202' && String(payload?.status) !== '200') {
        throw new Error(payload?.message)
      }
      toast.success(
        payload?.message || 'Revert job started successfully.'
      )
      setConfirmRevert(null)
      fetchLogs()
    } catch (error) {
      toast.error(getMessage(error))
    }
  }

  const updateGridRow = event => {
    const { colDef, newValue, rowIndex } = event
    setReplacements(rows =>
      rows.map((row, index) =>
        index === rowIndex ? { ...row, [colDef.field]: newValue || '' } : row
      )
    )
  }

  const handleCellFocused = params => {
    const column = params.api.getColumn(params.column)
    const colDef = column?.getColDef()

    if (!colDef?.editable || params.rowIndex == null) {
      return
    }

    setTimeout(() => {
      if (params.api.getEditingCells().length > 0) return
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: column.getColId()
      })
    }, 50)
  }

  const handleCellClicked = params => {
    const column = params.api.getColumn(params.column)
    const colDef = column?.getColDef()

    if (params.rowIndex == null || !colDef?.editable) {
      return
    }

    if (params.api.getEditingCells().length > 0) return

    setTimeout(() => {
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: column.getColId()
      })
    }, 50)
  }

  const handleGridReady = params => {
    params.api.setFocusedCell(0, 'old_standard_code')
    params.api.startEditingCell({
      rowIndex: 0,
      colKey: 'old_standard_code'
    })
  }

  return (
    <ClaimsBulkSidebarLayout>
      <div className='w-full'>
        <div className='mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center'>
          <div className='flex items-center gap-2'>
            {isReplacePage ? (
              <div
                onClick={() => navigate('/claims/item-bulk-replace')}
                className='bg-[#0070C2] hover:bg-[#0070C2]/80 cursor-pointer rounded-full p-1'
                title='Back to item replacement logs'
              >
                <MdArrowBack
                  className={
                    isRTL
                      ? 'text-white h-4 w-4 rotate-180'
                      : 'text-white h-4 w-4'
                  }
                />
              </div>
            ) : null}
            <div>
              <h1 className='text-lg font-semibold text-black/70'>
                {isReplacePage
                  ? 'Replace Claim Item Codes'
                  : ''}
              </h1>
              <p className='text-[11px] text-slate-500'>
                {isReplacePage
                  ? 'Bulk replace item standard codes & descriptions in itemsPreAuth for claims within a date range.'
                  : ''}
              </p>
            </div>
          </div>
          {!isReplacePage && (
            <AppButton
              label='Replace Codes'
              startIcon={<Add />}
              onClick={() => navigate('/claims/item-bulk-replace/replace')}
            />
          )}
          {isReplacePage && (
            <div className='flex items-center gap-3'>
              <div className='flex gap-1 rounded-md border border-slate-200 p-0.5'>
                {['manual', 'excel'].map(mode => (
                  <button
                    key={mode}
                    type='button'
                    onClick={() => setInputMode(mode)}
                    className={`rounded px-3 py-1.5 text-xs font-medium ${inputMode === mode
                      ? 'bg-[#0070C2] text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                      }`}
                  >
                    {mode === 'excel'
                      ? 'Update Items by Excel'
                      : 'Manual Replace'}
                  </button>
                ))}
              </div>
              {inputMode === 'excel' && (
                <button
                  type='button'
                  onClick={downloadTemplate}
                  disabled={downloadingTemplate}
                  className='rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-wait'
                >
                  {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className='w-full'>
          {isReplacePage && (
            <div>
              <div className='flex flex-wrap items-center gap-3 bg-white px-4 py-2.5 rounded-md border border-slate-200 mb-2'>
                <CustomFilterInput
                  label={
                    <span>
                      {t('From Date')} <span className='text-red-500'>*</span>
                    </span>
                  }
                  type='date'
                  name='fromDate'
                  id='fromDate'
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  placeholder={t('From Date')}
                  className='w-full sm:w-[180px]'
                />
                <CustomFilterInput
                  label={
                    <span>
                      {t('To Date')} <span className='text-red-500'>*</span>
                    </span>
                  }
                  type='date'
                  name='toDate'
                  id='toDate'
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  placeholder={t('To Date')}
                  className='w-full sm:w-[180px]'
                />
              </div>

              {inputMode === 'manual' ? (
                <div className='mt-2 relative'>
                  {isFieldMenuOpen && (
                    <div
                      ref={fieldMenuRef}
                      className={`absolute ${isRTL ? 'left-2' : 'right-2'} top-10 z-20 w-[260px] rounded border border-gray-200 bg-white shadow-lg`}
                    >
                      <div className='border-b border-gray-100 px-3 py-2 text-xs font-semibold text-gray-700'>
                        Add Field
                      </div>
                      <div className='max-h-[260px] overflow-auto py-1'>
                        {FIELD_GROUPS.map(group => {
                          const isAdded = selectedFieldKeys.includes(group.key)
                          return (
                            <button
                              key={group.key}
                              type='button'
                              disabled={isAdded}
                              className={`block w-full px-3 py-2 text-start text-xs ${isAdded
                                ? 'cursor-not-allowed bg-gray-50 text-gray-400'
                                : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              onClick={() => handleAddFieldGroup(group.key)}
                            >
                              {group.title}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  <div className='ag-theme-quartz item-bulk-replace-grid h-[calc(100vh-270px)] w-full overflow-hidden rounded-md border border-slate-200'>
                    <AgGridReact
                      ref={gridRef}
                      columnDefs={mappingColumns}
                      rowData={replacements}
                      defaultColDef={defaultColDef}
                      enableRtl={isRTL}
                      animateRows
                      stopEditingWhenCellsLoseFocus
                      undoRedoCellEditing
                      undoRedoCellEditingLimit={50}
                      suppressRowClickSelection
                      onCellValueChanged={updateGridRow}
                      onCellFocused={handleCellFocused}
                      onCellClicked={handleCellClicked}
                      onGridReady={handleGridReady}
                    />
                  </div>
                  <div className='mt-1 flex justify-end'>
                    <button
                      type='button'
                      className='flex items-center gap-1 text-xs text-[#0070C2] underline disabled:text-slate-400'
                      onClick={() =>
                        setReplacements(rows => [
                          ...rows,
                          { ...emptyReplacement(), row_no: rows.length + 1 }
                        ])
                      }
                      disabled={replacing}
                    >
                      <Add fontSize='small' /> Add New Row
                    </button>
                  </div>
                </div>
              ) : (
                <div className='mt-2'>
                  <div
                    onDrop={handleFileDrop}
                    onDragOver={e => {
                      e.preventDefault()
                      setDragOver(true)
                    }}
                    onDragLeave={() => setDragOver(false)}
                    className={`relative rounded-lg border-2 border-dashed px-4 py-6 flex flex-col items-center justify-center text-center transition-colors cursor-pointer ${dragOver
                      ? 'border-[#0070C2] bg-blue-50'
                      : file
                        ? 'border-[#0070C2] bg-blue-50'
                        : 'border-slate-300 bg-slate-50 hover:border-[#0070C2] hover:bg-blue-50/40'
                      }`}
                  >
                    {file ? (
                      <>
                        <button
                          type='button'
                          onClick={e => {
                            e.stopPropagation()
                            setFile(null)
                          }}
                          className='absolute top-2 right-2 text-slate-400 hover:text-red-500'
                        >
                          <FaTimes size={16} />
                        </button>
                        <FileUploadOutlined className='text-3xl mb-2 text-[#0070C2]' />
                        <p className='text-xs font-semibold text-slate-700 truncate max-w-full px-8'>
                          {file.name}
                        </p>
                      </>
                    ) : (
                      <>
                        <FileUploadOutlined
                          className={`text-3xl mb-2 ${dragOver ? 'text-[#0070C2]' : 'text-slate-400'
                            }`}
                        />
                        <p className='text-xs font-semibold text-slate-600'>
                          Drag and drop your Excel file here
                        </p>
                        <p className='text-[10px] text-slate-400 mt-0.5'>
                          Supported: .xlsx, .xls
                        </p>
                        <input
                          type='file'
                          hidden
                          id='excel-upload'
                          accept='.xls,.xlsx'
                          onChange={e => {
                            setFile(e.target.files?.[0] || null)
                            setDragOver(false)
                          }}
                        />
                        <label htmlFor='excel-upload' className='mt-3'>
                          <AppButton
                            label='Browse files'
                            variant='outlined'
                            bgColor='transparent'
                            onClick={() =>
                              document.getElementById('excel-upload').click()
                            }
                          />
                        </label>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className='mt-2 flex justify-end gap-2 border-t border-slate-200 pt-2'>
                <AppButton
                  label='Reset'
                  variant='outlined'
                  bgColor='transparent'
                  onClick={() => {
                    setFromDate(defaultDates.fromDate)
                    setToDate(defaultDates.toDate)
                    setReplacements(createGridRows())
                    setFile(null)
                    setActiveJob(null)
                  }}
                  disabled={replacing || uploading}
                />
                <AppButton
                  label={
                    inputMode === 'manual' ? 'Replace All' : 'Upload Item Excel'
                  }
                  startIcon={
                    inputMode === 'manual' ? (
                      <ReplayOutlined />
                    ) : (
                      <FileUploadOutlined />
                    )
                  }
                  onClick={inputMode === 'manual' ? submitManual : submitExcel}
                  isLoading={replacing || uploading}
                  loadingText='Processing...'
                />
              </div>

              {activeJob && (
                <ActiveJobCard
                  job={activeJob}
                  onStopPolling={handleStopPolling}
                />
              )}
            </div>
          )}

          {!isReplacePage && (
            <div className='pt-0'>
              <AppTable
                columns={logColumns}
                data={logs}
                loading={loadingLogs}
                actions={false}
                bodyRowPadding='7px'
                LoaderComponent={
                  <TableSkeletonLoader columnCount={8} rowCount={8} />
                }
              />
              <div className='mt-2'>
                <Pagination
                  currentPage={logsPage}
                  totalPages={logPages}
                  onPageChange={newPage => fetchLogs(newPage)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <AppModal
        open={Boolean(confirmRevert)}
        handleClose={() => setConfirmRevert(null)}
        title='Revert Bulk Replace'
      >
        <p className='text-sm text-slate-600'>
          Are you sure you want to revert this bulk item replacement job (
          <span className='font-mono font-semibold'>
            {confirmRevert?.job_id}
          </span>
          )? Rows modified after replacement will be skipped for safety.
        </p>
        <div className='mt-4 flex justify-end gap-2'>
          <AppButton
            label='Cancel'
            variant='outlined'
            bgColor='transparent'
            onClick={() => setConfirmRevert(null)}
            disabled={reverting}
          />
          <AppButton
            label='Revert'
            bgColor='#dc2626'
            onClick={revertJob}
            isLoading={reverting}
            loadingText='Reverting...'
          />
        </div>
      </AppModal>

      <style>
        {`
        .item-bulk-replace-grid {
          --ag-border-color: #9ca3af;
          --ag-row-border-color: #cbd5e1;
          --ag-header-background-color: #e5e7eb;
          --ag-header-foreground-color: #111827;
          --ag-odd-row-background-color: #f8fafc;
          --ag-cell-horizontal-border: solid #cbd5e1;
          --ag-row-height: 30px;
          --ag-header-height: 32px;
          --ag-font-size: 12px;
        }

        .item-bulk-replace-grid .ag-root-wrapper,
        .item-bulk-replace-grid .ag-header,
        .item-bulk-replace-grid .ag-cell {
          border-color: #cbd5e1;
        }

        .item-bulk-replace-grid .ag-header-cell,
        .item-bulk-replace-grid .ag-header-group-cell {
          border-right: 1px solid #9ca3af;
          font-weight: 700;
        }

        .item-bulk-replace-grid .ag-cell {
          border-right: 1px solid #cbd5e1;
          border-bottom: 1px solid #e2e8f0;
          padding-left: 6px;
          padding-right: 6px;
        }

        .item-bulk-replace-grid .ag-cell-focus {
          outline: none;
        }

        .item-bulk-replace-grid .ag-cell-inline-editing {
          padding: 0 !important;
          outline: 2px solid #0070c2;
          outline-offset: -2px;
        }

        .item-bulk-replace-grid .ag-cell-edit-wrapper,
        .item-bulk-replace-grid .ag-cell-editor,
        .item-bulk-replace-grid .ag-text-field,
        .item-bulk-replace-grid .ag-wrapper,
        .item-bulk-replace-grid .ag-input-wrapper,
        .item-bulk-replace-grid input.ag-input-field-input {
          width: 100% !important;
          height: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box;
        }

        .item-bulk-replace-grid input.ag-input-field-input {
          padding: 0 6px !important;
        }
      `}
      </style>
    </ClaimsBulkSidebarLayout>
  )
}
